import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Send,
  CheckCircle,
  XCircle,
  Handshake,
  Loader2,
  Trash2,
  ClockPlus,
  MoreHorizontal,
  FileText,
  Eye,
  Star,
} from 'lucide-react';
import type { Candidate, JobOffer, PipelineStage } from '../../models/job';
import type { ExamConfig } from '../../types/job.types';
import { Button } from '../ui/atoms/Button';
import { DropdownMenu } from '../ui/organisms/DropdownMenu';
import { CandidatePipelineActionDialogs } from './candidate-actions/CandidatePipelineActionDialogs';
import { CandidateMoveToMenu } from './candidate-actions/CandidateMoveToMenu';
import { useCandidateDownload } from '../../hooks/useCandidateDownload';
import { useCandidatePipelineMutations } from '../../hooks/useCandidatePipelineMutations';
import { useCandidatePipelineDnd } from '../../hooks/useCandidatePipelineDnd';
import { useCandidateStageMove } from '../../hooks/useCandidateStageMove';
import { useJobPipeline } from '../../hooks/useJobOffers';
import {
  getCandidatePipelineActions,
  getShortlistStage,
  listPipelineActionIds,
  type CandidatePipelineActionId,
  type CandidateContextualActionId,
} from '../../utils/candidate-actions';
import { findStageByType, stageWillNotify } from '../../utils/pipeline-stages';
import { createGTMTracking, getGTMAttributes } from '../../utils/gtm/gtm-tracking';
import type { GtmEventKey } from '../../utils/gtm/gtm-tracking';
import { cn } from '../../utils/cn';

export type CandidateActionsVariant = 'table' | 'footer' | 'card' | 'kanban';

export interface CandidateActionsProps {
  candidate: Candidate;
  job: JobOffer;
  examConfig: ExamConfig;
  onStatusUpdate?: () => void;
  className?: string;
  onExtendInterview?: (
    interviewId: string,
    candidateName: string,
    currentExpiration: string | null
  ) => void;
  requiresReview?: boolean;
  onDeleteCandidate?: (candidateId: string, candidateName: string) => void;
  /** Opens candidate profile / details drawer. */
  onViewDetails?: () => void;
  /** table/card = muted icons ; footer = labeled buttons ; kanban = stage CTA + more */
  variant?: CandidateActionsVariant;
  /** GTM origin for this surface */
  origin?: string;
}

const ACTION_LABEL_KEYS = {
  send_interview: 'tooltips.send_interview',
  accept: 'tooltips.approve_candidate',
  reject: 'tooltips.decline_candidate',
  hire: 'tooltips.hire_candidate',
} as const satisfies Record<
  CandidatePipelineActionId,
  | 'tooltips.send_interview'
  | 'tooltips.approve_candidate'
  | 'tooltips.decline_candidate'
  | 'tooltips.hire_candidate'
>;

const FOOTER_TOOLTIP_KEYS: Partial<
  Record<
    CandidatePipelineActionId,
    | 'tooltips.send_interview_email'
    | 'tooltips.send_interview_request'
    | 'tooltips.send_rejection_email'
  >
> = {
  send_interview: 'tooltips.send_interview_email',
  accept: 'tooltips.send_interview_request',
  reject: 'tooltips.send_rejection_email',
};

const FOOTER_TOOLTIP_KEYS_AUTOMATION_OFF: Partial<
  Record<
    CandidatePipelineActionId,
    | 'tooltips.move_to_interviewing_no_email'
    | 'tooltips.move_to_accepted_no_email'
    | 'tooltips.move_to_rejected_no_email'
  >
> = {
  send_interview: 'tooltips.move_to_interviewing_no_email',
  accept: 'tooltips.move_to_accepted_no_email',
  reject: 'tooltips.move_to_rejected_no_email',
};

const ACTION_TO_STAGE_TYPE: Partial<
  Record<CandidatePipelineActionId, string>
> = {
  send_interview: 'interviewing',
  accept: 'accepted',
  reject: 'rejected',
};

const ACTION_GTM = {
  send_interview: 'SEND_INTERVIEW',
  accept: 'APPROVE_CANDIDATE',
  reject: 'DECLINE_CANDIDATE',
  hire: 'HIRE_CANDIDATE',
} as const satisfies Record<CandidatePipelineActionId, GtmEventKey>;

type KanbanPrimary =
  | { kind: 'pipeline'; id: CandidatePipelineActionId }
  | { kind: 'extend' }
  | { kind: 'view' };

/**
 * Stage-specific card primary CTA from the shared action catalog.
 * Driven by pipelineStageSemantic (domain), never by column display order —
 * reordering columns does not change what a candidate can do.
 *
 * Interviewing: Extend is the first surface action when a link exists,
 * then Approve / Decline as secondary icons.
 */
function resolveKanbanPrimary(
  catalog: ReturnType<typeof getCandidatePipelineActions>
): KanbanPrimary {
  if (catalog.leading.some((item) => item.id === 'extend')) {
    return { kind: 'extend' };
  }
  if (catalog.primary) {
    return { kind: 'pipeline', id: catalog.primary.id };
  }
  return { kind: 'view' };
}

export const CandidateActions: React.FC<CandidateActionsProps> = ({
  candidate,
  job,
  examConfig,
  onStatusUpdate,
  className = '',
  onExtendInterview,
  requiresReview = false,
  onDeleteCandidate,
  onViewDetails,
  variant = 'table',
  origin = 'candidate_actions',
}) => {
  const { t } = useTranslation(['candidates']);
  const {
    downloadCV,
    downloadAssessment,
    isDownloadingCV,
    isDownloadingAssessment,
  } = useCandidateDownload(candidate, job);
  const mutations = useCandidatePipelineMutations({
    job,
    examConfig,
    requiresReview,
    onStatusUpdate,
    onDeleteCandidate,
    onExtendInterview,
  });
  const { data: pipeline } = useJobPipeline(job.id);
  const stages = pipeline?.stages;
  const pipelineDnd = useCandidatePipelineDnd(job.id);
  const { moveToStage } = useCandidateStageMove({ mutations, pipelineDnd });

  const catalog = useMemo(
    () => getCandidatePipelineActions(candidate),
    [candidate]
  );

  const footerTooltipFor = useCallback(
    (id: CandidatePipelineActionId) => {
      const stageType = ACTION_TO_STAGE_TYPE[id];
      if (!stageType) return FOOTER_TOOLTIP_KEYS[id];
      // Until pipeline loads, keep the historical "will send email" tooltips.
      if (!stages) return FOOTER_TOOLTIP_KEYS[id];
      const willNotify = stageWillNotify(findStageByType(stages, stageType));
      return willNotify
        ? FOOTER_TOOLTIP_KEYS[id]
        : FOOTER_TOOLTIP_KEYS_AUTOMATION_OFF[id];
    },
    [stages]
  );

  const canDownloadAssessment = Boolean(candidate.hasAssessmentAvailable);

  const pipelineIds = listPipelineActionIds(catalog);
  const isBusy =
    mutations.isAccepting ||
    mutations.isRejecting ||
    mutations.isHiring ||
    mutations.isSendingInterview ||
    pipelineDnd.moveCard.isPending;

  const moveCandidate = (stage: PipelineStage, fromPosition?: number) =>
    moveToStage(candidate, stage, { fromPosition, announceMove: true });

  const shortlistedStage = getShortlistStage(catalog, stages);

  const runPipelineAction = useCallback(
    (id: CandidatePipelineActionId) => {
      switch (id) {
        case 'send_interview':
          mutations.openSendInterview(candidate);
          break;
        case 'accept':
          mutations.openAccept(candidate);
          break;
        case 'reject':
          mutations.openReject(candidate);
          break;
        case 'hire':
          mutations.openHire(candidate);
          break;
      }
    },
    [mutations, candidate]
  );

  const runContextual = useCallback(
    (id: CandidateContextualActionId) => {
      switch (id) {
        case 'delete':
          mutations.requestDelete(candidate);
          break;
        case 'extend':
          mutations.requestExtend(candidate);
          break;
        default:
          break;
      }
    },
    [mutations, candidate]
  );

  const iconForAction = (id: CandidatePipelineActionId) => {
    switch (id) {
      case 'send_interview':
        return Send;
      case 'accept':
        return CheckCircle;
      case 'reject':
        return XCircle;
      case 'hire':
        return Handshake;
    }
  };

  const loadingForAction = (id: CandidatePipelineActionId) =>
    (id === 'send_interview' && mutations.isSendingInterview) ||
    (id === 'accept' && mutations.isAccepting) ||
    (id === 'hire' && mutations.isHiring) ||
    (id === 'reject' && mutations.isRejecting);

  const renderDownloadMenuItems = () => {
    const items = [
      <DropdownMenu.Item
        key="download-cv"
        {...getGTMAttributes(
          createGTMTracking('DOWNLOAD_CV', { origin, target: 'cv' })
        )}
        onClick={downloadCV}
        disabled={isDownloadingCV}
      >
        {isDownloadingCV ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <FileText className="h-4 w-4" />
        )}
        {t('candidates:downloads.cv')}
      </DropdownMenu.Item>,
    ];

    if (canDownloadAssessment) {
      items.push(
        <DropdownMenu.Item
          key="download-assessment"
          {...getGTMAttributes(
            createGTMTracking('DOWNLOAD_ASSESSMENT', {
              origin,
              target: 'assessment',
            })
          )}
          onClick={downloadAssessment}
          disabled={isDownloadingAssessment}
        >
          {isDownloadingAssessment ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <FileText className="h-4 w-4" />
          )}
          {t('candidates:downloads.assessment_pdf')}
        </DropdownMenu.Item>
      );
    }

    return items;
  };

  const kanbanPrimary = resolveKanbanPrimary(catalog);

  /** Accept / Reject shown inline; when Extend is primary, Accept joins them. */
  const kanbanSecondaryIds: Array<'accept' | 'reject'> = [];
  if (kanbanPrimary.kind === 'extend' && catalog.primary?.id === 'accept') {
    kanbanSecondaryIds.push('accept');
  }
  for (const item of catalog.direct) {
    if (item.id !== 'accept' && item.id !== 'reject') continue;
    if (!kanbanSecondaryIds.includes(item.id)) {
      kanbanSecondaryIds.push(item.id);
    }
  }

  const pipelineMenuIds = pipelineIds.filter((id) => {
    if (kanbanPrimary.kind === 'pipeline' && kanbanPrimary.id === id) {
      return false;
    }
    if (kanbanSecondaryIds.includes(id as 'accept' | 'reject')) {
      return false;
    }
    return true;
  });

  // Extend is the kanban primary when available — keep it out of More.
  const showExtendInMenu =
    catalog.leading.some((item) => item.id === 'extend') &&
    kanbanPrimary.kind !== 'extend';

  const renderMenuItem = (id: CandidateContextualActionId) => {
    if (id === 'download') {
      return (
        <React.Fragment key="download">{renderDownloadMenuItems()}</React.Fragment>
      );
    }
    if (id === 'delete') {
      return (
        <DropdownMenu.Item
          key={id}
          {...getGTMAttributes(
            createGTMTracking('DELETE_CANDIDATE', {
              origin,
              target: 'open_modal',
            })
          )}
          variant="destructive"
          onClick={() => runContextual('delete')}
        >
          <Trash2 className="h-4 w-4" />
          {t('candidates:tooltips.delete_candidate_menu')}
        </DropdownMenu.Item>
      );
    }
    return null;
  };

  const ActionsMenu = (
    <DropdownMenu>
      <DropdownMenu.Trigger asChild>
        <Button
          type="icon"
          intent="muted"
          size="xs"
          icon={<MoreHorizontal />}
          title={t('candidates:tooltips.more_actions')}
        />
      </DropdownMenu.Trigger>
      <DropdownMenu.Content align="end">
        {onViewDetails ? (
          <DropdownMenu.Item
            key="view-details"
            onClick={onViewDetails}
          >
            <Eye className="h-4 w-4" />
            {t('candidates:tooltips.view_details', {
              defaultValue: 'View details',
            })}
          </DropdownMenu.Item>
        ) : null}
        {showExtendInMenu ? (
          <DropdownMenu.Item
            key="extend"
            {...getGTMAttributes(
              createGTMTracking('EXTEND_INTERVIEW', {
                origin,
                target: 'open_modal',
              })
            )}
            onClick={() => runContextual('extend')}
          >
            <ClockPlus className="h-4 w-4" />
            {t('candidates:tooltips.extend_interview')}
          </DropdownMenu.Item>
        ) : null}
        {pipelineMenuIds.map((id) => {
          const Icon = iconForAction(id);
          return (
            <DropdownMenu.Item
              key={id}
              {...getGTMAttributes(
                createGTMTracking(ACTION_GTM[id], {
                  origin,
                  target: 'open_modal',
                })
              )}
              disabled={isBusy || (id === 'send_interview' && requiresReview)}
              onClick={() => runPipelineAction(id)}
            >
              <Icon className="h-4 w-4" />
              {t(ACTION_LABEL_KEYS[id])}
            </DropdownMenu.Item>
          );
        })}
        {catalog.contextual.map((item) => renderMenuItem(item.id))}
      </DropdownMenu.Content>
    </DropdownMenu>
  );

  const isFooter = variant === 'footer';
  const footerPrimaryAppearance = {
    type: 'icon',
    intent: 'secondary',
    size: 'md',
  } as const;
  const secondaryAppearance = isFooter
    ? ({
        type: 'contained',
        intent: 'muted',
        size: 'md',
        className: 'w-10 !px-0',
      } as const)
    : ({
        type: 'icon',
        intent: 'muted',
        size: variant === 'kanban' ? 'xs' : 'sm',
      } as const);

  const renderIconPipelineButton = (
    id: CandidatePipelineActionId,
    options?: { isPrimary?: boolean }
  ) => {
    const Icon = iconForAction(id);
    const loading = loadingForAction(id);
    const label = t(ACTION_LABEL_KEYS[id]);
    const footerTooltipKey = isFooter ? footerTooltipFor(id) : undefined;
    // Shortlist, when shown, is the footer's single dark primary action.
    const appearance =
      isFooter && options?.isPrimary && !shortlistedStage
        ? footerPrimaryAppearance
        : secondaryAppearance;
    return (
      <Button
        key={id}
        {...getGTMAttributes(
          createGTMTracking(ACTION_GTM[id], {
            origin,
            target: 'open_modal',
          })
        )}
        onClick={() => runPipelineAction(id)}
        disabled={isBusy || (id === 'send_interview' && requiresReview)}
        loading={loading}
        {...appearance}
        icon={loading ? <Loader2 className="animate-spin" /> : <Icon />}
        title={footerTooltipKey ? t(footerTooltipKey) : label}
        tooltipVariant={isFooter ? 'top' : undefined}
        aria-label={label}
      />
    );
  };

  const moveToMenu = (
    <CandidateMoveToMenu
      candidate={candidate}
      stages={stages ?? []}
      onMove={moveCandidate}
      disabled={isBusy}
      size={isFooter ? 'md' : 'sm'}
    />
  );

  const shortlistLabel = t('candidates:tooltips.shortlist_candidate');
  const ShortlistButton = shortlistedStage ? (
    <Button
      key="shortlist"
      {...getGTMAttributes(
        createGTMTracking('SHORTLIST_CANDIDATE', {
          origin,
          target: 'shortlisted',
        })
      )}
      onClick={() => moveCandidate(shortlistedStage)}
      disabled={isBusy}
      {...(isFooter ? footerPrimaryAppearance : secondaryAppearance)}
      icon={<Star />}
      title={shortlistLabel}
      tooltipVariant={isFooter ? 'top' : undefined}
      aria-label={shortlistLabel}
    />
  ) : null;

  const LeadingButtons = catalog.leading.map((item) => {
    if (item.id === 'shortlist') return ShortlistButton;
    if (item.id !== 'extend') return null;
    return (
      <Button
        key="extend"
        {...getGTMAttributes(
          createGTMTracking('EXTEND_INTERVIEW', {
            origin,
            target: 'open_modal',
          })
        )}
        onClick={() => runContextual('extend')}
        type="icon"
        intent="muted"
        size="sm"
        icon={<ClockPlus />}
        title={t('candidates:tooltips.extend_interview')}
      />
    );
  });

  const IconRow = (
    <div className="flex items-center gap-1">
      {LeadingButtons}
      {catalog.primary
        ? renderIconPipelineButton(catalog.primary.id, { isPrimary: true })
        : null}
      {catalog.direct.map((item) => renderIconPipelineButton(item.id))}
      {variant === 'table' ? moveToMenu : null}
      {catalog.contextual.length > 0 ? (
        <DropdownMenu>
          <DropdownMenu.Trigger asChild>
            <Button
              type="icon"
              intent="muted"
              size="sm"
              icon={<MoreHorizontal />}
              title={t('candidates:tooltips.more_actions')}
            />
          </DropdownMenu.Trigger>
          <DropdownMenu.Content align="end">
            {catalog.contextual.map((item) => renderMenuItem(item.id))}
          </DropdownMenu.Content>
        </DropdownMenu>
      ) : null}
    </div>
  );

  /** Meta CV: one filled primary (Shortlist when shown), soft direct icons, muted Move To (no delete / extend / downloads). */
  const FooterRow = (
    <>
      {ShortlistButton}
      {catalog.primary
        ? renderIconPipelineButton(catalog.primary.id, { isPrimary: true })
        : null}
      {catalog.direct.map((item) => renderIconPipelineButton(item.id))}
      {moveToMenu}
    </>
  );

  /**
   * Kanban primary CTA. Icon-only with the label as a hover tooltip: a card
   * column is too narrow for labels like "Send Interview Invitation", which
   * used to be clipped mid-word.
   */
  const renderKanbanPrimary = () => {
    if (kanbanPrimary.kind === 'view') {
      const label = t('candidates:tooltips.view_details', {
        defaultValue: 'View details',
      });
      return (
        <Button
          type="icon"
          intent="muted"
          size="xs"
          icon={<Eye />}
          title={label}
          aria-label={label}
          onClick={onViewDetails}
        />
      );
    }
    if (kanbanPrimary.kind === 'extend') {
      const label = t('candidates:tooltips.extend_interview');
      return (
        <Button
          type="icon"
          intent="muted"
          size="xs"
          icon={<ClockPlus />}
          title={label}
          aria-label={label}
          {...getGTMAttributes(
            createGTMTracking('EXTEND_INTERVIEW', {
              origin,
              target: 'open_modal',
            })
          )}
          onClick={() => runContextual('extend')}
        />
      );
    }
    const id = kanbanPrimary.id;
    const Icon = iconForAction(id);
    const loading = loadingForAction(id);
    const label = t(ACTION_LABEL_KEYS[id]);
    return (
      <Button
        type="icon"
        intent="muted"
        size="xs"
        icon={loading ? <Loader2 className="animate-spin" /> : <Icon />}
        title={label}
        aria-label={label}
        loading={loading}
        disabled={isBusy || (id === 'send_interview' && requiresReview)}
        {...getGTMAttributes(
          createGTMTracking(ACTION_GTM[id], {
            origin,
            target: 'open_modal',
          })
        )}
        onClick={() => runPipelineAction(id)}
      />
    );
  };

  const renderKanbanSecondary = (id: 'accept' | 'reject') => {
    const Icon = iconForAction(id);
    const loading = loadingForAction(id);
    return (
      <Button
        key={id}
        type="icon"
        intent="muted"
        size="xs"
        icon={loading ? <Loader2 className="animate-spin" /> : <Icon />}
        loading={loading}
        disabled={isBusy}
        title={t(ACTION_LABEL_KEYS[id])}
        aria-label={t(ACTION_LABEL_KEYS[id])}
        {...getGTMAttributes(
          createGTMTracking(ACTION_GTM[id], {
            origin,
            target: 'open_modal',
          })
        )}
        onClick={() => runPipelineAction(id)}
      />
    );
  };

  const KanbanRow = (
    <div className="flex w-full min-w-0 items-center justify-between gap-1.5">
      <div className="flex shrink-0 items-center gap-0.5">
        {ShortlistButton}
        {renderKanbanPrimary()}
      </div>
      <div className="flex shrink-0 items-center gap-0.5">
        {kanbanSecondaryIds.map((id) => renderKanbanSecondary(id))}
        {ActionsMenu}
      </div>
    </div>
  );

  return (
    <>
      <div
        className={cn(
          'flex items-center gap-1',
          variant === 'footer' && 'justify-end flex-wrap gap-2',
          variant === 'card' && 'w-full justify-end',
          variant === 'kanban' && 'w-full',
          className
        )}
      >
        {variant === 'footer'
          ? FooterRow
          : variant === 'kanban'
            ? KanbanRow
            : IconRow}
      </div>

      <CandidatePipelineActionDialogs
        candidate={candidate}
        mutations={mutations}
        origin={origin}
        stages={stages}
      />
    </>
  );
};
